/* eslint-disable react/jsx-sort-props */
/* eslint-disable react/no-children-prop */
import {
  Deactivated,
  FirebaseEmailActionHandler,
  GithubErrorPage,
  Home,
  Integrations,
  JoinWorkspace,
  LegacySprintsPage,
  LegacyTaskPage,
  Login,
  NotFound,
  Onboarding,
  OnboardingMobileNote,
  Profile,
  RequirementBuilder,
  Requirements,
  SprintDetails,
  SprintPage,
  TeamDetailsPage,
  Workspace,
} from 'components/pages';
import { DefinePage } from 'components/pages/Define';
import TaskPage from 'components/pages/TaskPage';
import {
  CheckoutSuccess,
  CompleteSprint,
  DeleteWorkspace,
  EditSprint,
  GithubLearnMore,
  LeaveTeam,
  LeaveWorkspace,
  NewRequirement,
  SetupIntegration,
  SprintInsights,
  SprintsSettings,
  StartSprint,
  TeamCreate,
} from 'components/routes';
import DeleteTeam from 'components/routes/DeleteTeam';
import Upgrade from 'components/routes/Upgrade/Upgrade';
import Firebase from 'firebase/app';
import React, { ReactElement, useEffect } from 'react';
import { useFirebase } from 'react-redux-firebase';
import { Redirect, Route, Router as ReactRouter, Switch, useHistory } from 'react-router-dom';
import { FeatureFlagCondition } from 'tools/helpers/FeatureFlagCondition';
import { segment } from 'tools/libraries/analytics';

import { browserHistory } from './history';
import InvitationContext from './InvitationContext';
import OrganizationTeamContext from './OrganizationTeamContext';
import { getPath, linkTo } from './paths';
import AuthenticatedRoute from './routes/AuthenticatedRoute';
import NonAuthenticatedRoute from './routes/NonAuthenticatedRoute';
import { OnboardedRoute } from './routes/OnboardedRoute';

const Logout: React.FC = () => {
  const firebase = useFirebase();
  const history = useHistory();

  useEffect(() => {
    firebase
      .logout()
      .then(() => history.replace('/'))
      // eslint-disable-next-line no-console
      .catch((err) => console.error('Logout failed', err));
  });

  return <div>Logout in progress...</div>;
};

export type OrgIdParam = { orgId: string };

export default function Router(): ReactElement {
  // track first load
  const { location } = browserHistory;
  Firebase.analytics().setCurrentScreen(location.pathname + location.search);

  useEffect(() => {
    segment.load();
    // track page transitions
    browserHistory.listen((loc) => {
      segment.page();
      Firebase.analytics().setCurrentScreen(loc.pathname + loc.search);
    });
  });

  return (
    <ReactRouter history={browserHistory}>
      <Switch>
        <NonAuthenticatedRoute path={getPath('login')} children={<Login />} />
        <Route path={getPath('logout')} children={<Logout />} />
        <AuthenticatedRoute path={getPath('deactivated')} children={<Deactivated />} />
        <Route path={getPath('emailActionHandler')} children={<FirebaseEmailActionHandler />} />
        <Route path={getPath('notFound')} children={<NotFound />} />
        <Route path={getPath('invite')} children={<JoinWorkspace />} />
        <AuthenticatedRoute path={getPath('setupIntegration')} children={<SetupIntegration />} />
        <AuthenticatedRoute exact path={getPath('onboarding')}>
          <Onboarding />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path={getPath('onboardingMobileNote')}>
          <OnboardingMobileNote />
        </AuthenticatedRoute>
        <InvitationContext>
          <OrganizationTeamContext>
            <OnboardedRoute exact path={getPath('home')} children={<Home />} />
            <OnboardedRoute exact path={getPath('homeTask')}>
              <Home />
            </OnboardedRoute>
            <OnboardedRoute exact path={getPath('githubLearnMore')}>
              <Home />
              <GithubLearnMore />
            </OnboardedRoute>
            <OnboardedRoute path={getPath('requirements')}>
              <FeatureFlagCondition flag='newSprintsPage' enabled={<DefinePage />} disabled={<Requirements />} />
            </OnboardedRoute>
            <OnboardedRoute path={getPath('newRequirement')} children={<NewRequirement />} />
            <OnboardedRoute exact path={getPath('createRequirement')}>
              <FeatureFlagCondition flag='newSprintsPage' enabled={<SprintPage />} disabled={<LegacySprintsPage />} />
              <NewRequirement createRequirementFromSprintsPage />
            </OnboardedRoute>
            <OnboardedRoute exact path={getPath('deleteTeam')}>
              <TeamDetailsPage />
              <DeleteTeam />
            </OnboardedRoute>
            <OnboardedRoute path={getPath('requirement')}>
              <FeatureFlagCondition flag='newSprintsPage' enabled={<DefinePage />} disabled={<RequirementBuilder />} />
            </OnboardedRoute>
            <OnboardedRoute path={getPath('requirementCreateTeam')} children={<TeamCreate />} />
            <FeatureFlagCondition
              flag='newSprintsPage'
              enabled={
                <>
                  <OnboardedRoute path={getPath('sprints_LEGACY')} children={<LegacySprintsPage />} />
                  <OnboardedRoute path={getPath('sprints')} children={<SprintPage />} />
                </>
              }
              disabled={<OnboardedRoute path={getPath('sprints')} children={<LegacySprintsPage />} />}
            />
            <FeatureFlagCondition
              flag='alphaMonetization'
              enabled={<OnboardedRoute path={getPath('upgrade')} children={<Upgrade />} />}
            />
            <FeatureFlagCondition
              flag='alphaMonetization'
              enabled={<OnboardedRoute path={getPath('checkoutSuccess')} children={<CheckoutSuccess />} />}
            />
            <OnboardedRoute path={getPath('sprintsSettings')} children={<SprintsSettings />} />
            <OnboardedRoute path={getPath('sprintStart')} children={<StartSprint />} />
            <OnboardedRoute path={getPath('sprintEdit')} children={<EditSprint />} />
            <OnboardedRoute path={getPath('sprintComplete')} children={<CompleteSprint />} />
            <OnboardedRoute path={getPath('sprintInsights')} children={<SprintInsights />} />
            <OnboardedRoute path={getPath('sprintDetails')} children={<SprintDetails />} />
            <OnboardedRoute path={getPath('sprintDetailsComplete')} children={<CompleteSprint />} />
            <OnboardedRoute path={getPath('sprintDetailsEdit')} children={<EditSprint />} />
            <OnboardedRoute path={getPath('sprintDetailsInsights')} children={<SprintInsights />} />
            <OnboardedRoute path={getPath('taskDetails')}>
              <FeatureFlagCondition flag='newSprintsPage' enabled={<TaskPage />} disabled={<LegacyTaskPage />} />
            </OnboardedRoute>
            <OnboardedRoute path={getPath('task')}>
              <FeatureFlagCondition flag='newSprintsPage' enabled={<DefinePage />} />
            </OnboardedRoute>
            <OnboardedRoute exact path={getPath('profile')} children={<Profile />} />
            <OnboardedRoute path={getPath('workspace')} children={<Workspace />} />
            <OnboardedRoute exact path={getPath('leaveWorkspace')}>
              <Workspace />
              <LeaveWorkspace />
            </OnboardedRoute>
            <OnboardedRoute exact path={getPath('deleteWorkspace')}>
              <Workspace />
              <DeleteWorkspace />
            </OnboardedRoute>
            <OnboardedRoute exact path={getPath('integrations')} children={<Integrations />} />
            <OnboardedRoute path={getPath('teamCreate')} children={<TeamCreate />} />
            <OnboardedRoute path={getPath('teamCreateSprintPlanning')} children={<TeamCreate />} />
            <OnboardedRoute path={getPath('teamCreateSprintDetails')} children={<TeamCreate />} />
            <OnboardedRoute path={getPath('teamDetails')} children={<TeamDetailsPage />} />
            <OnboardedRoute exact path={getPath('leaveTeam')}>
              <TeamDetailsPage />
              <LeaveTeam />
            </OnboardedRoute>
            <OnboardedRoute path={getPath('teamDetailsSprintPlanning')} children={<TeamDetailsPage />} />
            <OnboardedRoute path={getPath('teamDetailsSprintDetails')} children={<TeamDetailsPage />} />
            <OnboardedRoute path={getPath('deleteTeamWithConfirmation')} children={<DeleteTeam />} />
            <OnboardedRoute exact path={getPath('errorGithubRepoNotSelected')} children={<GithubErrorPage />} />
          </OrganizationTeamContext>
        </InvitationContext>
        <Route path='*' children={<Redirect to={linkTo('notFound')} />} />
      </Switch>
    </ReactRouter>
  );
}
